import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const DemoContainer = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  max-width: 1000px;
  margin: 3rem auto;
`;

const DemoHeader = styled.div`
  padding: 1.5rem;
  background: ${props => props.headerBackground || '#f8f9fa'};
  border-bottom: 1px solid #eaeaea;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DemoTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  color: #333;
`;

const TabGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Tab = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.isActive ? props.activeColor || '#0066cc' : 'transparent'};
  color: ${props => props.isActive ? 'white' : '#666'};
  border: ${props => props.isActive ? 'none' : '1px solid #ddd'};
  border-radius: 5px;
  cursor: pointer;
  font-weight: ${props => props.isActive ? '600' : '400'};
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.isActive ? props.activeColor || '#0066cc' : '#f0f4f8'};
  }
`;

const DemoContent = styled.div`
  padding: 2rem;
`;

const ResultContainer = styled(motion.div)`
  margin-top: 2rem;
  padding: 1.5rem;
  background: ${props => props.resultBackground || '#f8f9fa'};
  border-radius: 8px;
  border: 1px solid #eaeaea;
`;

const InputRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 200px;
`;

const Label = styled.label`
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #555;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${props => props.accentColor || '#0066cc'};
    box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${props => props.accentColor || '#0066cc'};
    box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
  resize: vertical;
  min-height: 100px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.accentColor || '#0066cc'};
    box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.1);
  }
`;

const RunButton = styled(motion.button)`
  padding: 0.75rem 1.5rem;
  background: ${props => props.buttonColor || '#0066cc'};
  color: white;
  border: none;
  border-radius: 5px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:disabled {
    background: #aaa;
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const CodeBlock = styled.pre`
  background: #1e1e2e;
  color: #f8f8f2;
  padding: 1.5rem;
  border-radius: 5px;
  overflow-x: auto;
  font-family: 'Fira Code', monospace;
  margin: 0;
`;

const ResultTitle = styled.h4`
  margin-top: 0;
  margin-bottom: 1rem;
  font-size: 1.2rem;
  color: #333;
`;

const InfoMessage = styled.div`
  padding: 1rem;
  background: ${props => props.type === 'error' ? '#fee2e2' : '#e0f2fe'};
  border-left: 4px solid ${props => props.type === 'error' ? '#ef4444' : '#3b82f6'};
  color: ${props => props.type === 'error' ? '#b91c1c' : '#1e40af'};
  margin-bottom: 1.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const LoadingSpinner = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="spinner">
    <style>{`
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
      .spinner {
        animation: spin 1s linear infinite;
      }
    `}</style>
    <circle cx="12" cy="12" r="10" strokeWidth="4" strokeDasharray="63" strokeDashoffset="42" />
  </svg>
);

const TabContent = ({ activeTab, demo }) => {
  switch (activeTab) {
    case 'api':
      return <ApiDemo demo={demo} />;
    case 'sdk':
      return <SdkDemo demo={demo} />;
    case 'cli':
      return <CliDemo demo={demo} />;
    default:
      return <ApiDemo demo={demo} />;
  }
};

const ApiDemo = ({ demo }) => {
  const [endpoint, setEndpoint] = useState(demo.endpoints?.[0] || '');
  const [method, setMethod] = useState('GET');
  const [payload, setPayload] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError(null);
    
    // Simulate API call
    setTimeout(() => {
      try {
        // This is a simulation - in a real app you'd make an actual API call
        // and handle the response
        if (endpoint === '/api/error') {
          throw new Error('API returned an error');
        }
        
        const mockResponse = {
          status: 200,
          data: {
            success: true,
            message: `Endpoint ${endpoint} called with ${method}`,
            timestamp: new Date().toISOString(),
            ...(payload ? { payload: JSON.parse(payload) } : {})
          }
        };
        
        setResult(mockResponse);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, 1500);
  };
  
  return (
    <form onSubmit={handleSubmit}>
      {error && <InfoMessage type="error">{error}</InfoMessage>}
      
      <InputRow>
        <InputGroup>
          <Label htmlFor="endpoint">API Endpoint</Label>
          <Select 
            id="endpoint"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            required
          >
            {demo.endpoints?.map((ep, index) => (
              <option key={index} value={ep}>{ep}</option>
            ))}
          </Select>
        </InputGroup>
        
        <InputGroup>
          <Label htmlFor="method">Method</Label>
          <Select 
            id="method"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          >
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="DELETE">DELETE</option>
          </Select>
        </InputGroup>
      </InputRow>
      
      <InputGroup>
        <Label htmlFor="payload">Request Payload (JSON)</Label>
        <Textarea 
          id="payload"
          value={payload}
          onChange={(e) => setPayload(e.target.value)}
          placeholder={method === 'GET' ? 'No payload needed for GET requests' : '{\n  "key": "value"\n}'}
          disabled={method === 'GET'}
        />
      </InputGroup>
      
      <RunButton 
        type="submit"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        disabled={loading}
      >
        {loading ? <LoadingSpinner /> : 'Run Request'}
      </RunButton>
      
      {result && (
        <ResultContainer
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <ResultTitle>Response</ResultTitle>
          <CodeBlock>{JSON.stringify(result, null, 2)}</CodeBlock>
        </ResultContainer>
      )}
    </form>
  );
};

const SdkDemo = ({ demo }) => {
  const [function_, setFunction] = useState(demo.functions?.[0] || '');
  const [params, setParams] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError(null);
    
    // Simulate SDK function call
    setTimeout(() => {
      try {
        // This is a simulation
        if (function_ === 'client.error') {
          throw new Error('SDK function threw an error');
        }
        
        const mockResponse = {
          success: true,
          functionCalled: function_,
          timestamp: new Date().toISOString(),
          ...(params ? { params: JSON.parse(params) } : {})
        };
        
        setResult(mockResponse);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, 1500);
  };
  
  return (
    <form onSubmit={handleSubmit}>
      {error && <InfoMessage type="error">{error}</InfoMessage>}
      
      <InputRow>
        <InputGroup>
          <Label htmlFor="function">SDK Function</Label>
          <Select 
            id="function"
            value={function_}
            onChange={(e) => setFunction(e.target.value)}
            required
          >
            {demo.functions?.map((fn, index) => (
              <option key={index} value={fn}>{fn}</option>
            ))}
          </Select>
        </InputGroup>
      </InputRow>
      
      <InputGroup>
        <Label htmlFor="params">Function Parameters (JSON)</Label>
        <Textarea 
          id="params"
          value={params}
          onChange={(e) => setParams(e.target.value)}
          placeholder='{\n  "param1": "value1",\n  "param2": "value2"\n}'
        />
      </InputGroup>
      
      <RunButton 
        type="submit"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        disabled={loading}
      >
        {loading ? <LoadingSpinner /> : 'Run Function'}
      </RunButton>
      
      {result && (
        <ResultContainer
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <ResultTitle>Result</ResultTitle>
          <CodeBlock>{JSON.stringify(result, null, 2)}</CodeBlock>
        </ResultContainer>
      )}
    </form>
  );
};

const CliDemo = ({ demo }) => {
  const [command, setCommand] = useState(demo.commands?.[0] || '');
  const [args, setArgs] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError(null);
    
    // Simulate CLI command execution
    setTimeout(() => {
      try {
        // This is a simulation
        if (command === 'error') {
          throw new Error('Command execution failed');
        }
        
        const mockOutput = `Executing: ${command} ${args}\n\n$ ${command} ${args}\nStarted command at ${new Date().toISOString()}\nProcessing...\nCommand completed successfully.`;
        
        setResult(mockOutput);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, 1500);
  };
  
  return (
    <form onSubmit={handleSubmit}>
      {error && <InfoMessage type="error">{error}</InfoMessage>}
      
      <InputRow>
        <InputGroup>
          <Label htmlFor="command">CLI Command</Label>
          <Select 
            id="command"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            required
          >
            {demo.commands?.map((cmd, index) => (
              <option key={index} value={cmd}>{cmd}</option>
            ))}
          </Select>
        </InputGroup>
      </InputRow>
      
      <InputGroup>
        <Label htmlFor="args">Command Arguments</Label>
        <Input 
          id="args"
          value={args}
          onChange={(e) => setArgs(e.target.value)}
          placeholder="--flag value -o option"
        />
      </InputGroup>
      
      <RunButton 
        type="submit"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        disabled={loading}
      >
        {loading ? <LoadingSpinner /> : 'Run Command'}
      </RunButton>
      
      {result && (
        <ResultContainer
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <ResultTitle>Command Output</ResultTitle>
          <CodeBlock>{result}</CodeBlock>
        </ResultContainer>
      )}
    </form>
  );
};

const InteractiveDemo = ({
  title = 'Interactive Demo',
  headerBackground = '#f8f9fa',
  accentColor = '#0066cc',
  resultBackground = '#f8f9fa',
  demo = {
    endpoints: ['/api/data', '/api/users', '/api/auth', '/api/error'],
    functions: ['client.getData()', 'client.createUser()', 'client.authenticate()', 'client.error()'],
    commands: ['project init', 'project deploy', 'project test', 'error']
  }
}) => {
  const [activeTab, setActiveTab] = useState('api');
  
  return (
    <DemoContainer>
      <DemoHeader headerBackground={headerBackground}>
        <DemoTitle>{title}</DemoTitle>
        <TabGroup>
          <Tab 
            isActive={activeTab === 'api'} 
            onClick={() => setActiveTab('api')}
            activeColor={accentColor}
          >
            API
          </Tab>
          <Tab 
            isActive={activeTab === 'sdk'} 
            onClick={() => setActiveTab('sdk')}
            activeColor={accentColor}
          >
            SDK
          </Tab>
          <Tab 
            isActive={activeTab === 'cli'} 
            onClick={() => setActiveTab('cli')}
            activeColor={accentColor}
          >
            CLI
          </Tab>
        </TabGroup>
      </DemoHeader>
      
      <DemoContent>
        <TabContent 
          activeTab={activeTab} 
          demo={demo}
        />
      </DemoContent>
    </DemoContainer>
  );
};

export default InteractiveDemo;
